import { useTranslation } from "react-i18next";
import { Parcel } from "@/mocks/parcels";

interface ShipmentProgressProps {
  parcel: Parcel;
}

const NORMAL_STATUS_ORDER: Record<string, number> = {
  Processing: 0,
  "Departed Origin": 1,
  "In Transit": 1,
  "Out for Delivery": 2,
  Delivered: 3,
};

function getStageIndex(status: string): number {
  if (status === "Exception") return -1;
  return NORMAL_STATUS_ORDER[status] ?? 0;
}

function getLastCompletedLocation(parcel: Parcel): string {
  const completed = parcel.timeline.filter((e) => e.completed && e.location);
  return completed.length > 0 ? completed[completed.length - 1].location : parcel.currentLocation;
}

function getLastCompletedDescription(parcel: Parcel): string {
  const completed = parcel.timeline.filter((e) => e.completed && e.description);
  return completed.length > 0 ? completed[completed.length - 1].description : "";
}

export default function ShipmentProgress({ parcel }: ShipmentProgressProps) {
  const { t } = useTranslation();

  const NORMAL_STAGES = [
    { key: "Processing", label: t("stage_processing"), icon: "ri-file-check-line" },
    { key: "In Transit", label: t("stage_in_transit"), icon: "ri-truck-line" },
    { key: "Out for Delivery", label: t("stage_out_for_delivery"), icon: "ri-e-bike-line" },
    { key: "Delivered", label: t("stage_delivered"), icon: "ri-checkbox-circle-line" },
  ];

  const isException = parcel.status === "Exception";
  const currentIdx = getStageIndex(parcel.status);
  const totalStages = NORMAL_STAGES.length - 1;
  const progressPct = isException ? 0 : Math.round((currentIdx / totalStages) * 100);
  const currentLocation = getLastCompletedLocation(parcel);
  const lastDesc = getLastCompletedDescription(parcel);

  return (
    <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-5">
      {/* Current location row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-400/20 text-emerald-300">
            <i className="ri-map-pin-2-fill text-sm" />
          </div>
          <div>
            <p className="text-slate-400 text-xs">{t("progress_current_location")}</p>
            <p className="text-white font-semibold text-sm">{currentLocation}</p>
          </div>
        </div>
        {lastDesc && (
          <p className="text-slate-400 text-xs max-w-xs text-right hidden sm:block">{lastDesc}</p>
        )}
      </div>

      {/* Exception notice */}
      {isException && (
        <div className="flex items-start gap-3 bg-amber-500/15 border border-amber-400/25 rounded-xl px-4 py-3 mb-5">
          <div className="w-5 h-5 flex items-center justify-center text-amber-400 shrink-0 mt-0.5">
            <i className="ri-information-line" />
          </div>
          <div>
            <p className="text-amber-300 text-sm font-semibold mb-0.5">{t("progress_action_required")}</p>
            <p className="text-amber-400/80 text-xs leading-relaxed">
              {t("progress_exception_desc")}
            </p>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {!isException && (
        <div className="relative mb-6">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-teal-300 rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="absolute right-0 -top-5 text-xs text-emerald-300 font-semibold">{progressPct}% {t("progress_complete")}</p>
        </div>
      )}

      {/* Stage steps */}
      <div className="flex items-start justify-between gap-1">
        {NORMAL_STAGES.map((stage, idx) => {
          const isCompleted = !isException && idx < currentIdx;
          const isCurrent = !isException && idx === currentIdx;
          const isPending = isException || idx > currentIdx;

          return (
            <div key={stage.key} className="flex flex-col items-center flex-1 min-w-0">
              {/* Icon circle */}
              <div className="relative w-full flex items-center justify-center mb-2">
                <div
                  className={`relative w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all shrink-0
                    ${isCompleted ? "bg-emerald-500 border-emerald-400 text-white" : ""}
                    ${isCurrent ? "bg-emerald-400 border-emerald-300 text-white" : ""}
                    ${isPending ? "bg-white/8 border-white/15 text-slate-600" : ""}
                  `}
                  style={isCurrent ? {
                    boxShadow: "0 0 0 4px rgba(52,211,153,0.25), 0 0 18px 4px rgba(52,211,153,0.35)",
                  } : undefined}
                >
                  <i className={`${stage.icon} text-sm`} />
                  {isCurrent && parcel.status !== "Delivered" && (
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-white border-2 border-emerald-400 animate-pulse" />
                  )}
                </div>
              </div>

              {/* Label */}
              <p
                className={`text-center text-xs leading-tight font-medium px-0.5
                  ${isCompleted ? "text-emerald-300" : ""}
                  ${isCurrent ? "text-emerald-300 font-bold" : ""}
                  ${isPending ? "text-slate-600" : ""}
                `}
              >
                {stage.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
